import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid, Slide } from '@mui/material';
import { IWorkItemDraftContext, WorkItemDraftProvider, useWorkItemDraft, useWorkItemPermissions, useWorkItemTemplate } from 'hooks/work-items';
import React, { useEffect, useMemo, useState } from 'react';
import { IWorkItemDraft } from 'models/work-items';
import { WorkItemCreateHeader } from './create';
import IStepItem from 'models/work-items/IStepItem';
import { TransitionProps } from '@mui/material/transitions';
import useWorkItemDraftOptions from 'hooks/work-items/useWorkItemDraftOptions';
import useWorkItemDraftStepValidation from 'hooks/work-items/useWorkItemDraftStepValidation';
import WorkItemCreateStepper from './create/WorkItemCreateStepper';
import useWorkItemDraftFieldUpdater from 'hooks/work-items/useWorkItemDraftFieldUpdater';
import { useRefresh } from 'hooks/shared';
import useWorkItemDraftFinalize from 'hooks/work-items/useWorkItemDraftFinalize';
import { DeleteDialog, Ico_Warning, useSnackbar } from '@sagecentral/core-ui';
import DialogMUI from '@mui/material/Dialog';

type WorkItemCreateLayoutProps = {
    draftId?: number;
    name?: string;
};

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const WorkItemCreateLayout: React.FC<WorkItemCreateLayoutProps> = (props) => {

    const { draftId } = useParams();
    let navigate = useNavigate();
    const location = useLocation();
    const permissions = useWorkItemPermissions();
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [confirmFinalize, setConfirmFinalize] = useState<boolean>(false);
    const { displaySnackbar } = useSnackbar();

    const [workItem, loading, updater, deleteDraft, error] = useWorkItemDraft(Number(draftId), displaySnackbar);
    const [options, optsLoading, getOptionsForStatus] = useWorkItemDraftOptions(undefined);

    const [ template, templateLoading ] = useWorkItemTemplate({
        templateId: Number(workItem?.templateId)
    });
    
    const [ activeStep, setActiveStep ] = useState<number>(0);

    const [ revalidateIndex, revalidate ] = useRefresh();

    const [ validateSteps, adjustSteps ] = useWorkItemDraftStepValidation(workItem);
    const [ validatedSteps, setValidatedSteps ] = useState<IStepItem[]>([]);

    const [ initialLoad, setInitialLoad ] = useState<boolean>(true);

    const [ updateVisit ] = useWorkItemDraftFieldUpdater('visited', workItem);

    const [finalizeAndCreate, loadingFinilaze] = useWorkItemDraftFinalize(async (resp) => {        
        if (resp.status == 400) {
            const details = await resp.json() as IStepItem[];
            
            adjustSteps(details);
            setValidatedSteps(details);

            for (var i = 0; i < details.length; i++) {
                if (!details[i].complete) {
                    navigateToStep(i);
                    break;
                }
            }
            return true;
        }

        return false;
    });
    
    useEffect(() => {
        let mounted = true;
        
        const fetchSteps = async () => {
            try {
                const steps = await validateSteps();
                if (mounted) {
                    setValidatedSteps(steps);
                    setInitialLoad(false);
                }
            } catch (error) {
                console.error("Error validating steps:", error);
                if (mounted) {
                    setInitialLoad(false);
                }
            }
        };
        
        fetchSteps();
        
        return () => {
            mounted = false;
        };
    }, [revalidateIndex, validateSteps]);

    useEffect(() => {
        revalidate();
    }, [ activeStep, workItem ]);

    const currentStep = useMemo(() => validatedSteps.find((step, index) => index === activeStep) ?? null, [ activeStep, validatedSteps ]);
    const isStepComplete = () => {
        if(currentStep?.key == 'basic-info'){
            return currentStep?.complete;
        }

        return true;
    }
    const isValid = useMemo(() => {
        if(draftId == null || Number(draftId) <= 0) {
            return false;
        }
        return validatedSteps.filter(s => !s.complete && Object.keys(s.fieldErrors ?? {}).length > 0).length === 0;
    }, [ validatedSteps ]);

    useEffect(() => {
        const idx = validatedSteps.findIndex((s) => location.pathname.endsWith(s.key));
        const step = validatedSteps[idx];
        
        if (step && step.visited == false) {
            updateVisit(step.key);      
        }

        setActiveStep(idx);
    }, [ location, validatedSteps ]);

    const value = useMemo<IWorkItemDraftContext>(() => ({
        workItem,
        template,
        options,
        activeStep: currentStep,
        getOptionsForStatus,        
        updateWorkItemDraft: (draft: IWorkItemDraft) => {
            updater(draft, false);
        },
        isStepComplete: (key: string) => {
            return true;
        },
        revalidate
    }), [workItem, options, currentStep ]);

    const baseUrl = useMemo(() =>
        draftId
            ? `/workitems/create/${draftId}/`
            : '/workitems/create/'
        , [draftId])

    const navigateToStep = (targetStep: number) => {
        var step = validatedSteps.find((step, index) => index === targetStep);
        if (step == null) {
            return;
        }

        var basicInfoStep = validatedSteps.find((step) => step.key === 'basic-info');

        if (basicInfoStep && basicInfoStep.complete || basicInfoStep?.key == step.key) {
            navigate(`${baseUrl}${step?.key}`);
        }
    }

    const handleDeleteWorkItem = () => {
        setConfirmDelete(true);
    }

    const handleDismissDelete = () => {
        setConfirmDelete(false);
    }

    const handleDeleteWorkItemConfirmed = () => {
        deleteDraft(Number(draftId));
        navigate('/create');
    }

    const handleNext = () => {
        navigateToStep(activeStep + 1);
    }

    const handleClose = () => {
        navigate('/create');
    }

    const nextStepName = useMemo(() => {
        var step = validatedSteps.find((step, index) => index === activeStep + 1);
        return step?.label ?? '';
    }, [ activeStep, validatedSteps ]);

    const handleFinalize = () => setConfirmFinalize(true);
    const handleDismissFinalize = () => setConfirmFinalize(false);

    const handleNotNowFinalize = async () => {
        const id = await finalizeAndCreate(draftId);
        setConfirmFinalize(false);
        if (id > 0) {
            navigate(`/workitems/${id}/overview`, { replace: false });
        }
    };

    const handleYesAssignWorkflow = async () => {
        const workitemId = await finalizeAndCreate(draftId);
        setConfirmFinalize(false);
        
        if (workitemId) {
            const returnUrl = `/workitems/${workitemId}/overview`;            
            window.history.pushState({}, "", returnUrl);
            
            const encodedReturnUrl = encodeURIComponent(window.location.origin + returnUrl);            
            const workflowViewUrl = process.env.REACT_APP_CLASSIC_URL + 
                `/WorkflowInstance/CreateWorkflowInstance?workItemID=${workitemId}&returnUrl=${encodedReturnUrl}`;
            window.location.href = workflowViewUrl;
        }
    };

    const canCreateWorkflow = useMemo(() => {
        return permissions?.canCreateWorkflow || false;
    }, [permissions]);

    return (
        <>
            <WorkItemDraftProvider value={value}>
                <Box sx={{ height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                    <WorkItemCreateHeader draftName={workItem?.name} />
                    <Box sx={{ height: '40px', marginBottom:'10px', display: 'flex', justifyContent: 'end' }}>
                        <Button variant='contained' onClick={handleFinalize} disabled={!isValid || initialLoad} sx={{ textTransform: 'none' }}>Finalize and Create</Button>    
                    </Box>  
                    {initialLoad && permissions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <WorkItemCreateStepper
                            activeStep={activeStep}
                            steps={validatedSteps}
                            onStepClick={navigateToStep}
                        />
                    )}
                    <Box sx={{ flexGrow: '1' }}>
                        <Grid container sx={{ minWidth: '100%' }}>
                            <Grid item xs={12}>
                                <Outlet />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ height: '40px', display: 'flex', mt: '15px', justifyContent: 'end' }}>
                        <Button variant='contained' color='error' disabled={!draftId} onClick={handleDeleteWorkItem}>Delete</Button>
                        <Button variant='contained' onClick={handleClose}>Close</Button>
                        {validatedSteps.length > 0 && activeStep + 1 !== validatedSteps.length ?                               
                            <Button variant='contained' disabled={!isStepComplete()} onClick={handleNext}>Next: {nextStepName + '  >'} </Button> 
                        : activeStep + 1 === validatedSteps.length ?
                        <Button variant='contained' onClick={handleFinalize} disabled={!isValid || initialLoad} sx={{ textTransform: 'none' }}>Finalize and Create</Button>
                        : <></> }
                    </Box>
                </Box>
            </WorkItemDraftProvider>

            <DeleteDialog
                nameOfItem="Work Item"
                onCancel={() => handleDismissDelete()}
                onDelete={() => { handleDeleteWorkItemConfirmed() }}
                open={confirmDelete}
            />

            <DialogMUI open={confirmFinalize} maxWidth={'lg'} TransitionComponent={Transition} keepMounted onClose={handleDismissFinalize}>
                <DialogTitle sx={{ textAlign: 'left', fontWeight: '600', fontSize: '17px' }}>
                    {<Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />}
                    <br />
                    Before You Finalize
                </DialogTitle>
                <DialogContent>
                    {canCreateWorkflow ? 'Would you like to assign a workflow to this work item now?' : 'Are you sure you want to finalize?'}
                </DialogContent>
                <DialogActions sx={{ minWidth: '460px' }} >
                    {canCreateWorkflow && <Button
                        fullWidth variant="outlined" onClick={handleNotNowFinalize} sx={{ textTransform: 'none' }}>No, I Want to Finalize
                    </Button>
                    ||
                    <Button
                        fullWidth variant="outlined" onClick={handleDismissFinalize} sx={{ textTransform: 'none' }}>No, Take Me Back to Draft
                    </Button>}
                    {canCreateWorkflow && <Button
                        disabled={loadingFinilaze}
                        endIcon={loadingFinilaze ?
                            <CircularProgress size={20}
                                sx={{ position: 'unset', color: (theme) => theme.palette.primary.contrastText }}
                            /> : null}
                        fullWidth variant="contained" onClick={handleYesAssignWorkflow}>Yes, Assign Workflow
                    </Button>
                    ||
                    <Button
                        disabled={loadingFinilaze}
                        endIcon={loadingFinilaze ?
                            <CircularProgress size={20}
                                sx={{ position: 'unset', color: (theme) => theme.palette.primary.contrastText }}
                            /> : null}
                        fullWidth variant="contained" onClick={handleNotNowFinalize}>Yes, I Want to Finalize
                    </Button>}
                </DialogActions>
            </DialogMUI>
        </>
    );
};
export default WorkItemCreateLayout;